import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone";

const TZ = "Europe/Tallinn";

dayjs.extend(timezone);

export const tallinnTimeToUtc = (timeStr: string): string => {
  const today = dayjs().format("YYYY-MM-DD");
  return dayjs.tz(`${today} ${timeStr}`, TZ).utc().format("HH:mm");
};

const QUARTER_MS = 15 * 60 * 1000;

const tallinnParts = new Intl.DateTimeFormat("en-GB", {
  timeZone: TZ,
  hourCycle: "h23",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

const toTallinn = (epochMs: number) => {
  const parts = Object.fromEntries(
    tallinnParts.formatToParts(epochMs).map((p) => [p.type, p.value]),
  );

  return {
    date: `${parts.year}-${parts.month}-${parts.day}`,
    hour: Number(parts.hour),
    minute: Number(parts.minute),
  };
};

const tallinnInstant = (date: string, hour: number) => {
  const wallAsUtc = Date.parse(
    `${date}T${String(hour).padStart(2, "0")}:00:00Z`,
  );

  return [3, 2]
    .map((offsetHours) => wallAsUtc - offsetHours * 60 * 60 * 1000)
    .find((instant) => {
      const local = toTallinn(instant);
      return local.date === date && local.hour === hour;
    })!;
};

const pad = (n: number) => String(n).padStart(2, "0");

export interface ForecastRow {
  start: string;
  end: string;
  /** 0..24 — the next day's first hour is 24. */
  hour: number;
  minute: number;
  repeatedHour: boolean;
}

// Stepped in UTC: dayjs tz().add() keeps the start offset and misses the DST change.
export const getForecastRowsForDate = (dateString: string): ForecastRow[] => {
  const nextDate = dayjs(dateString).add(1, "day").format("YYYY-MM-DD");
  const end = tallinnInstant(nextDate, 1);

  const rows: ForecastRow[] = [];
  const seen = new Set<string>();

  for (
    let instant = tallinnInstant(dateString, 0);
    instant < end;
    instant += QUARTER_MS
  ) {
    const local = toTallinn(instant);
    const next = toTallinn(instant + QUARTER_MS);
    const hour = local.date === dateString ? local.hour : local.hour + 24;
    const key = `${hour}:${local.minute}`;

    rows.push({
      start: `${pad(local.hour)}:${pad(local.minute)}`,
      end: `${pad(next.hour)}:${pad(next.minute)}`,
      hour,
      minute: local.minute,
      repeatedHour: seen.has(key),
    });
    seen.add(key);
  }

  return rows;
};

export const get15MinIntervalsForDate = (dateString: string) =>
  getForecastRowsForDate(dateString).map((row) => `${row.start} - ${row.end}`);

export const get15MinStartTimesForDate = (dateString: string) =>
  getForecastRowsForDate(dateString).map((row) => row.start);

export const get15MinStartTimesForDateInUTC = (dateString: string) => {
  // start at local midnight of the given date
  const start = dayjs(dateString).startOf("day");

  // build the next day's local "01:00" explicitly
  const nextDateStr = start.add(1, "day").format("YYYY-MM-DD");
  const end = dayjs(`${nextDateStr} 23:45`, "YYYY-MM-DD HH:mm");

  const startTimesUTC: string[] = [];
  let current = start;

  while (current.isBefore(end)) {
    // convert current to UTC before formatting
    const utcTime = current.utc().format("HH:mm");
    startTimesUTC.push(utcTime);

    current = current.add(15, "minute");
  }

  return startTimesUTC;
};
